// components/PhoneInput.tsx
import React, { forwardRef } from 'react';
import { Select, TextInput, Group, Box, Input } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import ReactCountryFlag from 'react-country-flag';

export interface PhoneInputValue {
  country: string;
  phone: string;
}
interface CountryOption {
  value: string;
  label: string;
  code: string;
}

const defaultCountries: CountryOption[] = [
  { value: '+1',   label: '+1',   code: 'US' }, // EUA
  { value: '+51',  label: '+51',  code: 'PE' }, // Peru
  { value: '+52',  label: '+52',  code: 'MX' }, // México
  { value: '+54',  label: '+54',  code: 'AR' }, // Argentina
  { value: '+55',  label: '+55',  code: 'BR' }, // Brasil
  { value: '+56',  label: '+56',  code: 'CL' }, // Chile
  { value: '+57',  label: '+57',  code: 'CO' }, // Colômbia
  { value: '+58',  label: '+58',  code: 'VE' }, // Venezuela
  { value: '+502', label: '+502', code: 'GT' }, // Guatemala
  { value: '+503', label: '+503', code: 'SV' }, // El Salvador
  { value: '+504', label: '+504', code: 'HN' }, // Honduras
  { value: '+505', label: '+505', code: 'NI' }, // Nicarágua
  { value: '+506', label: '+506', code: 'CR' }, // Costa Rica
  { value: '+507', label: '+507', code: 'PA' }, // Panamá
  { value: '+591', label: '+591', code: 'BO' }, // Bolívia
  { value: '+593', label: '+593', code: 'EC' }, // Equador
  { value: '+595', label: '+595', code: 'PY' }, // Paraguai
  { value: '+598', label: '+598', code: 'UY' }, // Uruguai
];

const FlagItem = forwardRef<HTMLDivElement, CountryOption>(
  ({ label, code, ...others }, ref) => (
    <div ref={ref} {...others} style={{ display: 'flex', alignItems: 'center' }}>
      <ReactCountryFlag countryCode={code} svg style={{ width: '1.2em', marginRight: 8 }} />
      <span>{label}</span>
    </div>
  )
);

export const PhoneInput: React.FC<{
  value: PhoneInputValue;
  onChange: (val: PhoneInputValue) => void;
}> = ({ value, onChange }) => {
  const { t } = useTranslation();
  const selected = defaultCountries.find((c) => c.value === value.country);

  return (
    <Input.Wrapper label={t('phone.label')}>
      <Group spacing="xs">
        <Box sx={{ flex: '0 0 120px' }}>
          <Select
            data={defaultCountries}
            value={value.country}
            onChange={(val) => onChange({ ...value, country: val! })}
            placeholder={t('phone.countryPlaceholder')}
            clearable
            itemComponent={FlagItem}
            icon={
              selected ? (
                <ReactCountryFlag
                  countryCode={selected.code}
                  svg
                  style={{ width: '1.2em' }}
                />
              ) : null
            }
            sx={{ width: '100%' }}
          />
        </Box>

        <Box sx={{ flex: '1' }}>
          <TextInput
            type="tel"
            placeholder={t('phone.placeholder')}
            value={value.phone}
            onChange={(e) => onChange({ ...value, phone: e.currentTarget.value })}
            sx={{ width: '100%' }}
          />
        </Box>
      </Group>
    </Input.Wrapper>
  );
};
